import type { PortalReferral } from '@/services/referralsService'
import { referralCommissionLabel } from './referralCommissionLabel'
import { ParceirosStatusBadge } from './ParceirosStatusBadge'

type Props = {
  rows: PortalReferral[]
  loading: boolean
  page: number
  pageSize: number
  total: number
  totalPages: number
  onPage: (page: number) => void
  onOpen: (row: PortalReferral) => void
}

export function ParceirosIndicacoesTable({
  rows,
  loading,
  page,
  pageSize,
  total,
  totalPages,
  onPage,
  onOpen,
}: Props) {
  return (
    <section className='overflow-hidden rounded-3xl border border-[var(--glass-border)] bg-[var(--bg-card)]'>
      <table className='w-full table-fixed text-left text-sm text-text-primary'>
        <colgroup>
          <col className='w-[24%]' />
          <col className='w-[24%]' />
          <col className='w-[16%]' />
          <col className='w-[36%]' />
        </colgroup>
        <thead className='text-xs font-medium uppercase tracking-wider text-text-muted'>
          <tr>
            <th className='px-6 py-3'>Nome</th>
            <th className='px-6 py-3'>Empresa</th>
            <th className='px-6 py-3'>Status</th>
            <th className='px-6 py-3 text-right'>Comissão</th>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={4} className='px-5 py-8 text-text-muted'>
                Carregando indicações…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={4} className='px-5 py-8 text-text-muted'>
                Nenhuma indicação ainda. Cadastre a primeira.
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr
              key={row.id}
              className='cursor-pointer border-t border-[var(--glass-border)] hover:bg-white/[0.03]'
              onClick={() => onOpen(row)}
            >
              <td className='px-6 py-3'>
                <button
                  type='button'
                  onClick={() => onOpen(row)}
                  className='text-left font-medium text-text-primary underline-offset-2 hover:underline'
                >
                  {row.nome?.trim() || '—'}
                </button>
              </td>
              <td className='px-6 py-3 text-text-primary'>
                <p>{row.empresa}</p>
                {row.site ? <p className='text-xs text-text-muted'>{row.site}</p> : null}
              </td>
              <td className='px-6 py-3'>
                <ParceirosStatusBadge status={row.publicStatus} />
              </td>
              <td className='px-6 py-3 text-right text-text-muted'>{referralCommissionLabel(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > 0 && (
        <div className='flex flex-wrap items-center justify-between gap-3 border-t border-[var(--glass-border)] px-6 py-3 text-sm text-text-muted'>
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total}
          </span>
          <div className='flex gap-2'>
            <button
              type='button'
              disabled={page <= 1 || loading}
              onClick={() => onPage(page - 1)}
              className='rounded-full border border-[var(--glass-border)] px-3 py-1.5 disabled:opacity-40'
            >
              Anterior
            </button>
            <span className='self-center text-xs'>
              {page} / {totalPages}
            </span>
            <button
              type='button'
              disabled={page >= totalPages || loading}
              onClick={() => onPage(page + 1)}
              className='rounded-full border border-[var(--glass-border)] px-3 py-1.5 disabled:opacity-40'
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
